import { useEffect, useId, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent, ReactNode, Ref } from 'react';
import {
  DATE_PRESETS,
  facetSuggestions,
  formatDay,
  matchPreset,
  presetRange,
  todayInZone,
} from './filter-model';
import type { DatePresetId, FilterBarFacet, FilterValues } from './filter-model';

export interface FilterBarOption {
  value: string;
  label: string;
}

/** A dropdown filter; `multiple` keeps the chosen values comma-joined under one key. */
export interface FilterBarSelect {
  key: string;
  label: string;
  options: FilterBarOption[];
  multiple?: boolean;
  /** The "no filter" entry, default "All". */
  allLabel?: string;
}

export interface FilterBarDate {
  fromKey: string;
  toKey: string;
  label?: string;
  presets?: DatePresetId[];
}

export interface FilterBarProps {
  /** Search facets: typing offers "Search <label> for: …" for each. */
  facets?: FilterBarFacet[];
  selects?: FilterBarSelect[];
  date?: FilterBarDate;
  /** The current filters — the list's query parameters. */
  values: FilterValues;
  onChange: (patch: FilterValues) => void;
  onReset: () => void;
  /** The zone the list's days are on (the store's — T18). */
  timeZone: string;
  placeholder?: string;
  /** The favourites menu (SavedFiltersMenu). */
  favorites?: ReactNode;
  /** Extra buttons, e.g. Export. */
  actions?: ReactNode;
  /** The search input, for a page's "/ to search" shortcut. */
  inputRef?: Ref<HTMLInputElement>;
  now?: Date;
}

interface Chip {
  id: string;
  label: string;
  value: string;
  tone: 'brand' | 'violet' | 'teal';
  clear: FilterValues;
}

const CHIP_TONE: Record<Chip['tone'], string> = {
  brand: 'bg-brand-100 text-brand-800 dark:bg-brand-900/50 dark:text-brand-100',
  violet: 'bg-violet-100 text-violet-800 dark:bg-violet-900/50 dark:text-violet-100',
  teal: 'bg-teal-100 text-teal-800 dark:bg-teal-900/50 dark:text-teal-100',
};

const CONTROL =
  'h-9 rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-900 ' +
  'px-3 text-sm text-neutral-800 dark:text-neutral-100 transition-all duration-200 ' +
  'hover:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-500';

const FIELD_LABEL =
  'block text-[11px] uppercase tracking-wider font-semibold text-neutral-500 dark:text-neutral-400 mb-1';

const CUSTOM = 'custom';

function SearchIcon() {
  return (
    <svg
      className="h-4 w-4 shrink-0 text-neutral-400"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <circle cx="11" cy="11" r="7" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

/** The chips for every active filter — facets, the date range, the dropdowns. */
export function filterChips(
  props: Pick<FilterBarProps, 'facets' | 'selects' | 'date' | 'values' | 'timeZone' | 'now'>,
): Chip[] {
  const { facets = [], selects = [], date, values, timeZone, now } = props;
  const chips: Chip[] = [];
  for (const f of facets) {
    const v = values[f.key];
    if (v)
      chips.push({
        id: f.key,
        label: f.label,
        value: v,
        tone: 'brand',
        clear: { [f.key]: undefined },
      });
  }
  if (date) {
    const from = values[date.fromKey];
    const to = values[date.toKey];
    if (from || to) {
      const preset = matchPreset(from, to, timeZone, now, date.presets);
      const text = preset
        ? (DATE_PRESETS.find((p) => p.id === preset)?.label ?? preset)
        : `${from ? formatDay(from) : '…'} – ${to ? formatDay(to) : '…'}`;
      chips.push({
        id: 'date',
        label: date.label ?? 'Date',
        value: text,
        tone: 'violet',
        clear: { [date.fromKey]: undefined, [date.toKey]: undefined },
      });
    }
  }
  for (const s of selects) {
    const raw = values[s.key];
    if (!raw) continue;
    const labels = raw.split(',').map((v) => s.options.find((o) => o.value === v)?.label ?? v);
    chips.push({
      id: s.key,
      label: s.label,
      value: labels.join(' or '),
      tone: 'teal',
      clear: { [s.key]: undefined },
    });
  }
  return chips;
}

function MultiSelect({
  select,
  value,
  onChange,
}: {
  select: FilterBarSelect;
  value: string;
  onChange: (v: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const chosen = value ? value.split(',') : [];

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const toggle = (v: string) => {
    const next = chosen.includes(v) ? chosen.filter((c) => c !== v) : [...chosen, v];
    onChange(
      select.options
        .map((o) => o.value)
        .filter((o) => next.includes(o))
        .join(','),
    );
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={select.label}
        onClick={() => setOpen((o) => !o)}
        className={`${CONTROL} min-w-[10rem] text-left flex items-center justify-between gap-2`}
      >
        <span className="truncate">
          {chosen.length === 0 ? (select.allLabel ?? 'All') : `${chosen.length} selected`}
        </span>
        <span aria-hidden="true" className="text-neutral-400">
          ▾
        </span>
      </button>
      {open && (
        <div className="absolute z-30 mt-1 min-w-full rounded-lg bg-white dark:bg-neutral-800 shadow-lg ring-1 ring-neutral-200 dark:ring-neutral-700 p-1 animate-fade-in">
          {select.options.map((o) => (
            <label
              key={o.value}
              className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-neutral-700 dark:text-neutral-200 hover:bg-brand-50 dark:hover:bg-neutral-700 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={chosen.includes(o.value)}
                onChange={() => toggle(o.value)}
                className="rounded border-neutral-300 text-brand-600 focus:ring-brand-500"
              />
              {o.label}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * One filter bar for every list page (T27): a single search box with Odoo-style
 * facets (typing offers "Search Customer for: …", "Search Mobile for: …"; each
 * choice becomes a removable chip and chips combine with AND), date presets on
 * the store's calendar plus a custom range, dropdown and multi-select filters,
 * chips for everything active, Reset, and a slot for the favourites menu.
 *
 * Controlled and stateless about the filters themselves: `values` are the
 * list's query parameters and every change goes out through `onChange`.
 */
export function FilterBar({
  facets = [],
  selects = [],
  date,
  values,
  onChange,
  onReset,
  timeZone,
  placeholder = 'Search…',
  favorites,
  actions,
  inputRef,
  now,
}: FilterBarProps) {
  const listId = useId();
  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [customDates, setCustomDates] = useState(false);

  const suggestions = useMemo(() => facetSuggestions(text, facets), [text, facets]);
  const chips = filterChips({ facets, selects, date, values, timeZone, now });

  const presetIds = date?.presets;
  const presets = presetIds ? DATE_PRESETS.filter((p) => presetIds.includes(p.id)) : DATE_PRESETS;
  const from = date ? values[date.fromKey] : undefined;
  const to = date ? values[date.toKey] : undefined;
  const preset = date ? matchPreset(from, to, timeZone, now, date.presets) : null;
  const dateChoice = customDates || ((from || to) && !preset) ? CUSTOM : (preset ?? '');

  const choose = (facet: FilterBarFacet) => {
    const v = text.trim();
    if (!v) return;
    onChange({ [facet.key]: v });
    setText('');
    setOpen(false);
    setHighlight(0);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' && suggestions.length) {
      e.preventDefault();
      setOpen(true);
      setHighlight((h) => (h + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp' && suggestions.length) {
      e.preventDefault();
      setHighlight((h) => (h - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === 'Enter' && suggestions.length) {
      e.preventDefault();
      choose(suggestions[Math.min(highlight, suggestions.length - 1)]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    } else if (e.key === 'Backspace' && !text && chips.length) {
      onChange(chips[chips.length - 1].clear);
    }
  };

  const onDateChoice = (choice: string) => {
    if (!date) return;
    if (choice === CUSTOM) {
      setCustomDates(true);
      return;
    }
    setCustomDates(false);
    const r = choice ? presetRange(choice as DatePresetId, timeZone, now) : null;
    onChange({ [date.fromKey]: r?.from, [date.toKey]: r?.to });
  };

  return (
    <section
      aria-label="Filters"
      className="rounded-xl bg-white dark:bg-neutral-800 shadow-md hover:shadow-lg transition-all duration-200 ring-1 ring-neutral-100 dark:ring-neutral-700 p-4 space-y-3 animate-fade-in"
    >
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start">
        {/* The one search box: chips for every active filter, then the input. */}
        <div className="relative flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-1.5 min-h-[2.5rem] rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-900 px-2.5 py-1.5 transition-all duration-200 focus-within:ring-2 focus-within:ring-brand-500 hover:border-brand-400">
            <SearchIcon />
            {chips.map((chip) => (
              <span
                key={chip.id}
                className={`inline-flex items-center gap-1 rounded-full pl-2.5 pr-1 py-0.5 text-xs font-semibold animate-fade-in ${CHIP_TONE[chip.tone]}`}
              >
                <span className="opacity-75">{chip.label}:</span>
                <span className="max-w-[16ch] truncate">{chip.value}</span>
                <button
                  type="button"
                  aria-label={`Remove ${chip.label} filter`}
                  onClick={() => {
                    if (chip.id === 'date') setCustomDates(false);
                    onChange(chip.clear);
                  }}
                  className="ml-0.5 h-4 w-4 rounded-full leading-none hover:bg-black/10 dark:hover:bg-white/10 transition-colors"
                >
                  ×
                </button>
              </span>
            ))}
            <input
              ref={inputRef}
              role="combobox"
              aria-label="Search"
              aria-expanded={open && suggestions.length > 0}
              aria-controls={listId}
              aria-autocomplete="list"
              value={text}
              placeholder={chips.length ? '' : placeholder}
              onChange={(e) => {
                setText(e.target.value);
                setOpen(true);
                setHighlight(0);
              }}
              onFocus={() => setOpen(true)}
              onBlur={() => setOpen(false)}
              onKeyDown={onKeyDown}
              className="flex-1 min-w-[8rem] bg-transparent border-0 p-1 text-sm text-neutral-900 dark:text-white placeholder:text-neutral-400 focus:outline-none focus:ring-0"
            />
          </div>
          {open && suggestions.length > 0 && (
            <ul
              id={listId}
              role="listbox"
              className="absolute z-30 left-0 right-0 mt-1 overflow-hidden rounded-lg bg-white dark:bg-neutral-800 shadow-lg ring-1 ring-neutral-200 dark:ring-neutral-700 animate-fade-in"
            >
              {suggestions.map((facet, i) => (
                <li
                  key={facet.key}
                  role="option"
                  aria-selected={i === highlight}
                  // mousedown, not click: the input's blur would close the list first.
                  onMouseDown={(e) => {
                    e.preventDefault();
                    choose(facet);
                  }}
                  onMouseEnter={() => setHighlight(i)}
                  className={`px-3 py-2 text-sm cursor-pointer transition-colors ${
                    i === highlight
                      ? 'bg-brand-50 text-brand-800 dark:bg-brand-900/40 dark:text-brand-100'
                      : 'text-neutral-700 dark:text-neutral-200'
                  }`}
                >
                  Search <span className="font-semibold">{facet.label}</span> for:{' '}
                  <span className="font-semibold text-neutral-900 dark:text-white">
                    {text.trim()}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2 shrink-0">
          {favorites}
          {actions}
          {chips.length > 0 && (
            <button
              type="button"
              onClick={() => {
                setCustomDates(false);
                onReset();
              }}
              className="h-9 rounded-lg px-3 text-sm font-semibold text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-all duration-200"
            >
              Reset
            </button>
          )}
        </div>
      </div>

      {(date || selects.length > 0) && (
        <div className="flex flex-wrap items-end gap-3">
          {date && (
            <div>
              <label className={FIELD_LABEL} htmlFor={`${listId}-date`}>
                {date.label ?? 'Date'}
              </label>
              <select
                id={`${listId}-date`}
                value={dateChoice}
                onChange={(e) => onDateChoice(e.target.value)}
                className={CONTROL}
              >
                <option value="">Any date</option>
                {presets.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.label}
                  </option>
                ))}
                <option value={CUSTOM}>Custom range…</option>
              </select>
            </div>
          )}
          {date && dateChoice === CUSTOM && (
            <>
              <div>
                <label className={FIELD_LABEL} htmlFor={`${listId}-from`}>
                  From
                </label>
                <input
                  id={`${listId}-from`}
                  type="date"
                  value={from ?? ''}
                  max={to ?? todayInZone(timeZone, now)}
                  onChange={(e) => onChange({ [date.fromKey]: e.target.value || undefined })}
                  className={CONTROL}
                />
              </div>
              <div>
                <label className={FIELD_LABEL} htmlFor={`${listId}-to`}>
                  To
                </label>
                <input
                  id={`${listId}-to`}
                  type="date"
                  value={to ?? ''}
                  min={from}
                  onChange={(e) => onChange({ [date.toKey]: e.target.value || undefined })}
                  className={CONTROL}
                />
              </div>
            </>
          )}
          {selects.map((s) => (
            <div key={s.key}>
              <span className={FIELD_LABEL} id={`${listId}-${s.key}`}>
                {s.label}
              </span>
              {s.multiple ? (
                <MultiSelect
                  select={s}
                  value={values[s.key] ?? ''}
                  onChange={(v) => onChange({ [s.key]: v || undefined })}
                />
              ) : (
                <select
                  aria-labelledby={`${listId}-${s.key}`}
                  value={values[s.key] ?? ''}
                  onChange={(e) => onChange({ [s.key]: e.target.value || undefined })}
                  className={CONTROL}
                >
                  <option value="">{s.allLabel ?? 'All'}</option>
                  {s.options.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
